import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Globe } from 'lucide-react';
import { toast } from 'react-hot-toast';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks/useAuth';
import { RegisterRequest } from '@/types';

const registerSchema = z.object({
  name: z
    .string()
    .min(1, 'テナント名を入力してください')
    .min(2, 'テナント名は2文字以上で入力してください')
    .max(100, 'テナント名は100文字以内で入力してください'),
  email: z
    .string()
    .min(1, 'メールアドレスを入力してください')
    .email('有効なメールアドレスを入力してください'),
  password: z
    .string()
    .min(1, 'パスワードを入力してください')
    .min(8, 'パスワードは8文字以上で入力してください')
    .regex(/^(?=.*[a-zA-Z])(?=.*\d)/, 'パスワードは英数字を含む必要があります'),
  confirmPassword: z
    .string()
    .min(1, 'パスワード確認を入力してください'),
  subdomain: z
    .string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      return /^[a-z0-9-]+$/.test(val);
    }, 'サブドメインは英小文字、数字、ハイフンのみ使用できます')
    .refine((val) => {
      if (!val) return true;
      return val.length >= 3 && val.length <= 50;
    }, 'サブドメインは3-50文字で入力してください'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'パスワードが一致しません',
  path: ['confirmPassword'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

export const RegisterForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register: registerUser, isLoading } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const watchedName = watch('name');
  const suggestedSubdomain = watchedName
    ? watchedName.toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 20)
    : '';

  const onSubmit = async (data: RegisterFormData) => {
    try {
      const { confirmPassword, ...registerData } = data;
      if (!registerData.subdomain) {
        registerData.subdomain = suggestedSubdomain;
      }
      
      await registerUser(registerData as RegisterRequest);
      toast.success('アカウントの作成に成功しました！');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('アカウントの作成に失敗しました。入力内容を確認してください。');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          XTimeline SaaS
        </h1>
        <p className="text-gray-600">
          新しいアカウントを作成してください
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="relative">
          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            {...register('name')}
            type="text"
            placeholder="テナント名（会社名・サービス名）"
            className="pl-10"
            error={errors.name?.message}
            autoComplete="organization"
          />
        </div>

        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            {...register('email')}
            type="email"
            placeholder="メールアドレス"
            className="pl-10"
            error={errors.email?.message}
            autoComplete="email"
          />
        </div>

        <div className="relative">
          <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            {...register('subdomain')}
            type="text"
            placeholder={`サブドメイン（省略可、推奨: ${suggestedSubdomain}）`}
            className="pl-10"
            error={errors.subdomain?.message}
            helperText="https://[サブドメイン].x-timeline.net でアクセスできます"
          />
        </div>

        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            {...register('password')}
            type={showPassword ? 'text' : 'password'}
            placeholder="パスワード（8文字以上、英数字含む）"
            className="pl-10 pr-10"
            error={errors.password?.message}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            ) : (
              <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            )}
          </button>
        </div>

        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            {...register('confirmPassword')}
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="パスワード確認"
            className="pl-10 pr-10"
            error={errors.confirmPassword?.message}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? (
              <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            ) : (
              <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            )}
          </button>
        </div>

        <Button
          type="submit"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
        >
          アカウント作成
        </Button>

        <div className="text-center space-y-2">
          <p className="text-sm text-gray-600">
            既にアカウントをお持ちの方は{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
            >
              ログイン
            </Link>
          </p>
          <p className="text-xs text-gray-500">
            簡単に登録したい場合は{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
            >
              簡易登録フォーム
            </Link>
            をご利用ください
          </p>
        </div>
      </form>

      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="text-center text-xs text-gray-500">
          <p>アカウントを作成することで、</p>
          <p>
            <Link to="/terms" className="underline hover:text-gray-700">
              利用規約
            </Link>
            {' '}と{' '}
            <Link to="/privacy" className="underline hover:text-gray-700">
              プライバシーポリシー
            </Link>
            に同意したものとみなされます
          </p>
        </div>
      </div>
    </div>
  );
};